<template>
  <div id="pageCommodato" @click="handleClickOutside">
    <v-container fluid>
      <!-- Estadísticas básicas -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="4">
          <v-card class="stat-card h-100">
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <v-avatar color="primary lighten-4" size="52" class="mr-4">
                  <v-icon color="primary" size="28">mdi-text-box-multiple-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h5 font-weight-bold">{{ totalItems }}</div>
                  <div class="text-subtitle-2 grey--text text--darken-1">Comodatos totales</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Vista principal -->
      <v-row>
        <v-col cols="12">
          <v-card class="movement-card elevation-2">
            <!-- Header mejorado -->
            <v-toolbar color="primary" dark flat>
              <v-icon class="mr-2">mdi-text-box-multiple-outline</v-icon>
              <v-toolbar-title v-if="!$vuetify.breakpoint.mdAndDown">
                Lista de comodatos
              </v-toolbar-title>
              <v-spacer></v-spacer>

              <!-- Búsqueda para escritorio -->
              <v-col cols="3" class="mr-3" v-if="!$vuetify.breakpoint.mdAndDown">
                <v-text-field v-model="filter.search" label="Buscar comodato..." prepend-inner-icon="mdi-magnify"
                  hide-details clearable rounded single-line filled dense dark @input="applyFilter"></v-text-field>
              </v-col>

              <!-- Búsqueda para móvil -->
              <v-tooltip top v-if="$vuetify.breakpoint.mdAndDown && !showSearch">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="toggleSearch">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
                <span>Buscar</span>
              </v-tooltip>

              <v-col cols="12" v-if="showSearch && $vuetify.breakpoint.mdAndDown">
                <v-text-field ref="searchInput" v-model="filter.search" label="Buscar comodato..."
                  prepend-inner-icon="mdi-magnify" hide-details clearable rounded single-line filled dense dark
                  class="search-input-mobile" @click.stop @input="applyFilter"
                  @keydown.esc="showSearch = false"></v-text-field>
              </v-col>

              <!-- Menú de filtros -->
              <filter-menu title="Filtrar comodatos" :current-filter="currentFilter" @canceled="rollbackFilter()"
                @filtered="commitFilter()">
                <v-row>
                  <v-col>
                    <v-text-field v-model="filter['vehicle.iic_number']" label="Número IIC" clearable outlined
                      hide-details="auto" class="mb-4"></v-text-field>

                    <v-text-field v-model="filter['brand']" label="Marca" clearable outlined hide-details="auto"
                      class="mb-4"></v-text-field>

                    <v-select v-model="filter['source_location_id']" :items="LOCATIONS" item-text="name" item-value="id"
                      label="Origen" clearable outlined hide-details="auto" class="mb-4"></v-select>

                    <v-text-field v-model="filter['model']" label="Modelo" clearable outlined
                      hide-details="auto"></v-text-field>
                  </v-col>

                  <v-col>
                    <v-text-field v-model="filter['plate']" label="Matrícula" clearable outlined
                      hide-details="auto" class="mb-4"></v-text-field>

                    <v-text-field v-model="filter['frame_number']" label="Bastidor" clearable outlined
                      hide-details="auto" class="mb-4"></v-text-field>

                    <v-select v-model="filter['destination_location_id']" :items="LOCATIONS" item-text="name"
                      item-value="id" label="Destino" clearable outlined hide-details="auto" class="mb-4"
                      ></v-select>
                  </v-col>
                </v-row>
              </filter-menu>

              <!-- Botón actualizar -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="refreshDataTable" v-on="on" class="mr-2" color="white" :disabled="loadingItems">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Actualizar lista</span>
              </v-tooltip>
            </v-toolbar>

            <!-- Filtros rápidos bajo la barra de herramientas -->
            <v-card-text class="py-2 px-4">
              <v-row class="align-center justify-center">
                <v-col cols="auto" class="ml-auto">
                  <v-btn-toggle v-model="viewMode" mandatory dense color="primary">
                    <v-btn value="cards" small>
                      <v-icon small>mdi-view-grid-outline</v-icon>
                      Tarjetas
                    </v-btn>
                    <v-btn value="table" small>
                      <v-icon small>mdi-table</v-icon>
                      Tabla
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Filtros aplicados -->
              <v-row v-if="hasActiveFilters" class="mt-1 justify-center">
                <v-col cols="12">
                  <div class="d-flex align-center flex-wrap justify-center">
                    <span class="text-caption mr-2 grey--text">Filtros aplicados:</span>
                    <v-chip v-if="filter['vehicle.iic_number']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['vehicle.iic_number'] = null; commitFilter()">
                      IIC: {{ filter['vehicle.iic_number'] }}
                    </v-chip>
                    <v-chip v-if="filter['brand']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['brand'] = null; commitFilter()">
                      Marca: {{ filter['brand'] }}
                    </v-chip>
                    <v-chip v-if="filter['model']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['model'] = null; commitFilter()">
                      Modelo: {{ filter['model'] }}
                    </v-chip>
                    <v-chip v-if="filter['plate']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['plate'] = null; commitFilter()">
                      Matrícula: {{ filter['plate'] }}
                    </v-chip>
                    <v-chip v-if="filter['frame_number']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['frame_number'] = null; commitFilter()">
                      Bastidor: {{ filter['frame_number'] }}
                    </v-chip>
                    <v-chip v-if="filter['source_location_id']" x-small class="mr-1 mb-1" close color="indigo"
                      text-color="white" @click:close="filter['source_location_id'] = null; commitFilter()">
                      Origen: {{ getLocationName(filter['source_location_id']) }}
                    </v-chip>
                    <v-chip v-if="filter['destination_location_id']" x-small class="mr-1 mb-1" close color="indigo"
                      text-color="white" @click:close="filter['destination_location_id'] = null; commitFilter()">
                      Destino: {{ getLocationName(filter['destination_location_id']) }}
                    </v-chip>
                    <v-chip v-if="filter['status']" x-small class="mr-1 mb-1" close
                      :color="getStatusColor(filter['status'])" text-color="white"
                      @click:close="filter['status'] = null; commitFilter()">
                      {{ translateStatus(filter['status']) }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Estado de carga -->
            <div v-if="loadingItems" class="pa-3">
              <!-- Skeleton loaders para la vista de tabla -->
              <template v-if="viewMode === 'table'">
                <v-skeleton-loader type="table-heading" class="mb-2"></v-skeleton-loader>
                <v-skeleton-loader type="table-row@6"></v-skeleton-loader>
              </template>

              <!-- Skeleton loaders para vista de tarjetas -->
              <v-row v-else>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="n in 8" :key="`skeleton-${n}`">
                  <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </div>

            <!-- Vista de tabla -->
            <v-card-text v-else-if="viewMode === 'table'" class="pa-0">
              <v-data-table :headers="headers" :items="MOVIMIENTOS" :options.sync="pagination"
                :server-items-length="totalItems" :loading="loadingItems" :footer-props="footer" :key="tableId"
                class="elevation-0" :multi-sort="false">
                <!-- Columna de vehículo -->
                <template v-slot:item.vehicle="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar color="blue-grey lighten-4" size="36" class="mr-3">
                      <v-icon size="18" color="blue-grey darken-1">mdi-car</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.vehicle.brand }} {{ item.vehicle.model }}</div>
                      <div class="caption grey--text text--darken-1">
                        IIC: {{ item.vehicle.iic_number || 'N/A' }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Columna de matrícula -->
                <template v-slot:item.plate_number="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-car-info</v-icon>
                    {{ item.vehicle.plate_number || 'Sin matrícula' }}
                  </div>
                </template>

                <!-- Columna de bastidor -->
                <template v-slot:item.frame_number="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-identifier</v-icon>
                    {{ item.vehicle.frame_number || 'Sin bastidor' }}
                  </div>
                </template>

                <!-- Columna de fecha inicio -->
                <template v-slot:item.delivery_date="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-calendar-start</v-icon>
                    {{ formatDateHours(getMovementDate(item)) }}
                  </div>
                </template>

                <!-- Columna de fecha fin -->
                <template v-slot:item.end_date="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-calendar-end</v-icon>
                    {{ item.reception_datetime ? formatDateHours(item.reception_datetime) : formatDateHours(item.end_date) }}
                  </div>
                </template>

                <!-- Columna de responsable -->
                <template v-slot:item.responsible="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-account-tie</v-icon>
                    {{ item.responsible ? item.responsible.name : 'No asignado' }}
                  </div>
                </template>                <!-- Columna de conductor -->
                <template v-slot:item.driver="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-account-hard-hat</v-icon>
                    <span v-if="item.crane_id">
                      <v-icon small color="blue darken-1" class="mr-1">mdi-truck-trailer</v-icon>
                      <span class="blue--text text--darken-2">{{ getGruaName(item.crane_id) }}</span>
                    </span>
                    <span v-else-if="item.transportation === 'YO MISMO'">
                      <v-icon small color="green darken-1" class="mr-1">mdi-account</v-icon>
                      <span class="green--text text--darken-2">{{ item.responsible ? item.responsible.name : 'Usuario actual' }}</span>
                    </span>
                    <span v-else-if="item.driver">
                      {{ item.driver.name }} {{ item.driver.lastname }}
                      <span v-if="item.driver.dni" class="grey--text ml-1">({{ item.driver.dni }})</span>
                    </span>
                    <span v-else>No asignado</span>
                  </div>
                </template>

                <!-- Columna de acciones -->
                <template v-slot:item.action="{ item }">
                  <div class="d-inline-flex action-buttons">
                    <!-- Botón Ver Detalles -->
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="info"
                          :to="{ name: 'MovimientoDetail', params: { movimiento_id: item.id } }" v-on="on">
                          <v-icon>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver detalles</span>
                    </v-tooltip>

                    <!-- Botón Descargar PDF -->
                    <v-tooltip top v-if="item.status !== 'unreported' && !item.crane_id">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="primary" @click="downloadComodato(item)" v-on="on">
                          <v-icon>mdi-file-pdf-box</v-icon>
                        </v-btn>
                      </template>
                      <span>Descargar comodato</span>
                    </v-tooltip>

                  </div>
                </template>

                <!-- Sin resultados -->
                <template v-slot:no-data>
                  <div class="text-center pa-6">
                    <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron comodatos</h3>
                    <p class="subtitle-1 grey--text mb-4">
                      Intenta con otros filtros o agrega un nuevo movimiento
                    </p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Vista de tarjetas -->
            <v-card-text v-else class="pa-3">
              <v-row>
                <v-col cols="12" sm="6" md="4" v-for="item in MOVIMIENTOS" :key="item.id">
                  <v-card outlined hover class="movement-item-card h-100">
                    <v-card-text class="pa-4">

                      <!-- Vehículo -->
                      <div class="d-flex mb-4">
                        <v-avatar size="56" color="blue-grey lighten-4" class="mr-3">
                          <v-icon size="32" color="blue-grey darken-1">mdi-car</v-icon>
                        </v-avatar>
                        <div>
                          <div class="subtitle-1 font-weight-medium mb-1">
                            {{ item.vehicle.brand }} {{ item.vehicle.model }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            <v-icon x-small class="mr-1">mdi-card-bulleted-outline</v-icon>
                            {{ item.vehicle.plate_number || 'Sin matrícula' }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            <v-icon x-small class="mr-1">mdi-card-account-details-outline</v-icon>
                            IIC: {{ item.vehicle.iic_number || 'N/A' }}
                          </div>
                        </div>
                      </div>

                      <!-- Información básica -->
                      <v-list dense class="pa-0 transparent">
                        <!-- Matrícula -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-car-info</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <span class="caption font-weight-medium grey--text">Matrícula:</span>
                              {{ item.vehicle.plate_number || 'Sin matrícula' }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Bastidor -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-identifier</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <span class="caption font-weight-medium grey--text">Bastidor:</span>
                              {{ item.vehicle.frame_number || 'Sin bastidor' }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Fecha inicio -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-calendar-start</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <span class="caption font-weight-medium grey--text">Inicio:</span>
                              {{ formatDateHours(getMovementDate(item)) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Fecha fin -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-calendar-end</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <span class="caption font-weight-medium grey--text">Fin:</span>
                              {{ item.reception_datetime ? formatDateHours(item.reception_datetime) : formatDateHours(item.end_date) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Responsable -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-account-tie</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <span class="caption font-weight-medium grey--text">Responsable:</span>
                              {{ item.responsible ? item.responsible.name : 'No asignado' }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Conductor -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-account-hard-hat</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <span class="caption font-weight-medium grey--text">Conductor:</span>
                              <span v-if="item.crane_id" class="blue--text text--darken-2">
                                <v-icon x-small color="blue darken-1" class="mr-1">mdi-truck-trailer</v-icon>
                                 {{ getGruaName(item.crane_id) }}
                              </span>
                              <span v-else-if="item.transportation === 'YO MISMO'" class="green--text text--darken-2">
                                <v-icon x-small color="green darken-1" class="mr-1">mdi-account</v-icon>
                                {{ item.responsible ? item.responsible.name : 'Usuario actual' }}
                              </span>
                              <span v-else-if="item.driver">
                                {{ item.driver.name }} {{ item.driver.lastname }}
                                <span class="ml-1">{{ item.driver ? item.driver.dni : '' }}</span>
                              </span>
                              <span v-else>No asignado</span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>

                      <!-- Acciones -->
                      <v-card-actions class="pa-0 mt-3">
                        <v-btn text small color="info"
                          :to="{ name: 'MovimientoDetail', params: { movimiento_id: item.id } }">
                          <v-icon left small>mdi-eye</v-icon>
                          Ver detalles
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn v-if="item.status !== 'unreported'" text small color="primary"
                          @click="downloadComodato(item)">
                          <v-icon left small>mdi-file-pdf-box</v-icon>
                          PDF
                        </v-btn>
                      </v-card-actions>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              <!-- Sin resultados -->
              <v-row v-if="!MOVIMIENTOS.length && !loadingItems">
                <v-col cols="12" class="text-center pa-6">
                  <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron comodatos</h3>
                  <p class="subtitle-1 grey--text mb-4">
                    Intenta con otros filtros o agrega un nuevo comodato
                  </p>
                </v-col>
              </v-row>

              <!-- Paginación -->
              <v-row v-if="MOVIMIENTOS.length > 0">
                <v-col cols="12" class="d-flex justify-center align-center flex-wrap pa-2">
                  <div class="text-center d-flex align-center mr-4">
                    <span class="text-body-2 grey--text text--darken-1">Filas por página:</span>
                    <v-select v-model="pagination.itemsPerPage" :items="footer.itemsPerPageOptions" dense outlined
                      hide-details class="ml-2" style="width: 80px;" @change="onItemsPerPageChange"></v-select>
                  </div>

                  <v-pagination v-model="pagination.page" :length="Math.ceil(totalItems / pagination.itemsPerPage)"
                    :total-visible="7" circle @input="onPageChange"></v-pagination>

                  <span class="text-body-2 grey--text text--darken-1 ml-4">
                    {{ paginationText }}
                  </span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import moment from 'moment-timezone'
import { saveAs } from 'file-saver'
import FilterMenu from '../../components/FilterMenu'
import MovimientoService from '@/services/MovimientoService'
import GruaService from '@/services/GruaService'

export default {
  components: {
    FilterMenu
  },
  data: () => ({
    // Vista y carga
    loadingItems: false,
    totalItems: 0,
    tableId: 0,
    showSearch: false,
    manualViewMode: null,
    quickFilter: null,
    gruas: [], // Lista de grúas cargadas

    // datatable
    headers: [
      { text: 'Vehículo', align: 'start', value: 'vehicle', sortable: false },
      { text: 'Matrícula', value: 'plate_number', sortable: true },
      { text: 'Bastidor', value: 'frame_number', sortable: true },
      { text: 'Fecha inicio', value: 'delivery_date', sortable: true },
      { text: 'Fecha fin', value: 'end_date', sortable: true },
      { text: 'Responsable', value: 'responsible', sortable: false },
      { text: 'Conductor', value: 'driver', sortable: false },
      { text: 'Acciones', value: 'action', sortable: false, align: 'center', width: '160px' }
    ],
    footer: {
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      itemsPerPageText: 'Comodatos por página:',
      pageText: '{0}-{1} de {2}'
    },

    // filtro
    filter: {
      search: null,
      'vehicle.iic_number': null,
      'brand': null,
      'model': null,
      'plate': null,
      'frame_number': null,
      'source_location_id': null,
      'destination_location_id': null,
      'status': null
    },
    currentFilter: {
      search: null,
      'vehicle.iic_number': null,
      'brand': null,
      'model': null,
      'plate': null,
      'frame_number': null,
      'source_location_id': null,
      'destination_location_id': null,
      'status': null
    },
    estados: [
      { name: 'No Reportado', value: 'unreported' },
      { name: 'Reportado', value: 'reported' },
      { name: 'Recepcionado', value: 'transport' },
      { name: 'En Proceso', value: 'processing' },
      { name: 'Completado', value: 'completed' }
    ],

    // form
    visibleForm: false,
    editItem: null,
    roles: []
  }),

  async created () {
    // Bandera para evitar recarga durante la inicialización
    this.isInitializing = true

    this.roles = this.CURRENT_USER_ROLES

    // Cargar lista de grúas
    this.loadGruas()

    // Primero cargar ubicaciones
    this.$store.dispatch('LOAD_LOCATIONS').then(() => {
      // Luego cargar los datos con paginación
      this.loadData()

      // Desactivar la bandera de inicialización después de la carga inicial
      this.$nextTick(() => {
        this.isInitializing = false
      })
    }).catch(error => {
      console.error('Error al cargar ubicaciones:', error)
      this.isInitializing = false
    })
  },

  methods: {
    // Cargar lista de grúas 
    async loadGruas() {
      try {
        const response = await GruaService.get()
        this.gruas = response.data.data || []
      } catch (error) {
        console.error('Error al cargar grúas:', error)
      }
    },
    getMovementDate (item) {
      return item.delivery_date || item.datetime
    },
    // Obtener nombre de grúa por ID
    getGruaName(craneId) {
      if (!craneId) return 'No especificada'
      const grua = this.gruas.find(g => g.id === craneId)
      
      return grua ? `${grua.company}` : ``
    },
    loadData () {
      this.loadingItems = true
      const filterWithStatus = {
        ...this.filter,
        statuses: 'processing,completed',
        type: 'assignment'
      }

      this.$store.dispatch('LOAD_MOVIMIENTOS', filterWithStatus).then(result => {
        this.loadingItems = false
        // Asegurarse de que la página actual es válida
        const maxPage = Math.ceil(result / this.pagination.itemsPerPage)
        if (this.pagination.page > maxPage && maxPage > 0) {
          this.pagination.page = maxPage
        }
        this.totalItems = result
      }).catch(error => {
        console.error('Error al cargar movimientos:', error)
        this.$toast.error('Error al cargar los movimientos')
        this.loadingItems = false
      })
    },
    debouncedLoadData: _.debounce(function () {
      this.loadData()
    }, 500),
    updateDataTable ({ insert }) {
      this.visibleForm = false
      if (insert) {
        this.totalItems++
      }
      this.refreshDataTable()
      this.$toast.success(insert ? 'Cesión creado con éxito' : 'Cesión actualizado con éxito')
    },
    refreshDataTable () {
      this.tableId++
      this.loadData()
    },
    // Manejo de búsqueda en móvil
    toggleSearch () {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      }
    },
    handleClickOutside (event) {
      const searchInput = this.$refs.searchInput
      if (searchInput && !searchInput.$el.contains(event.target)) {
        this.showSearch = false
      }
    },

    // Filtros
    applyFilter () {
      this.debouncedLoadData()
    },
    commitFilter () {
      for (const key in this.currentFilter) {
        this.currentFilter[key] = this.filter[key]
      }
      this.loadData()
    },
    rollbackFilter () {
      for (const key in this.currentFilter) {
        this.filter[key] = this.currentFilter[key]
      }
    },
    resetFilters () {
      for (const key in this.filter) {
        this.filter[key] = null
      }
      this.quickFilter = null
      this.commitFilter()
    },
    applyQuickFilter (index) {
      if (index === null) {
        this.filter.status = null
      } else {
        const statusValues = ['unreported', 'reported', 'transport', 'processing', 'completed']
        this.filter.status = statusValues[index]
      }
      this.commitFilter()
    },
    // Recuperar nombre de ubicación
    getLocationName (locationId) {
      const location = this.LOCATIONS.find(l => l.id === locationId)
      return location ? location.name : 'Desconocido'
    },

    // Acciones
    showMovimientoForm (item) {
      this.editItem = item ? { ...item } : null
      this.visibleForm = true
    },
    showMovimientoAdd () {
      this.$router.push({ name: 'Escaneo' })
    },
    canAcceptMovimiento (item) {
      // Verificar si el movimiento puede ser aceptado solo cuando el estado es 'unreported'
      return item.status === 'unreported'
    },
    async acceptMovimiento (item) {
      try {
        this.$confirm(`¿Está seguro que desea cambiar el estado del movimiento a "${this.getNextStatusName(item.status)}"?`).then(async result => {
          if (result) {
            this.loadingItems = true
            let newStatus = 'reported'

            await MovimientoService.changeStatus(item.id, { status: newStatus }).then((response) => {
              this.loadingItems = false
              if (response.error) {
                this.$toast.error('Ocurrió un error al cambiar el estado del movimiento')
                return
              }
              this.$toast.success('Estado del movimiento actualizado con éxito')
              this.loadData()
            })
          }
        })
      } catch (error) {
        console.error('Error al cambiar el estado del movimiento:', error)
        this.$toast.error('Ocurrió un error al cambiar el estado del movimiento')
        this.loadingItems = false
      }
    },
    getNextStatusName (currentStatus) {
      const statusOrder = ['unreported', 'reported', 'transport', 'processing', 'completed']
      let currentStatusIndex = statusOrder.indexOf(currentStatus)
      if (currentStatusIndex !== -1 && currentStatusIndex < statusOrder.length - 1) {
        let nextStatus = statusOrder[currentStatusIndex + 1]
        return this.translateStatus(nextStatus)
      }
      return this.translateStatus(currentStatus)
    },

    // Formatos y traducciones
    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).format('DD/MM/YYYY')
    },
    formatDateHours (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },
    translateStatus (status) {
      const statusMap = {
        'unreported': 'No reportado',
        'reported': 'Reportado',
        'transport': 'Recepcionado',
        'processing': 'En proceso',
        'completed': 'Completado'
      }
      return statusMap[status] || status
    },
    onPageChange (page) {
      // Actualizar la página en la paginación
      this.pagination.page = page
      // Forzar la carga de datos inmediatamente después del cambio de página
      this.loadData()
    },
    onItemsPerPageChange (perPage) {
      // Actualizar items por página en la paginación
      this.pagination.itemsPerPage = perPage
      // Asegurarse de volver a la primera página al cambiar elementos por página
      this.pagination.page = 1
      // Forzar la carga de datos
      this.loadData()
    },
    getStatusColor (status) {
      const statusColorMap = {
        'unreported': 'red',
        'reported': 'orange',
        'transport': 'blue',
        'processing': 'purple',
        'completed': 'green'
      }
      return statusColorMap[status] || 'grey'
    },
    getStatusIcon (status) {
      const statusIconMap = {
        'unreported': 'mdi-alert-circle-outline',
        'reported': 'mdi-bell-ring-outline',
        'transport': 'mdi-truck-outline',
        'processing': 'mdi-progress-clock',
        'completed': 'mdi-check-circle-outline'
      }
      return statusIconMap[status] || 'mdi-help-circle-outline'
    },

    async downloadComodato(item) {
    this.$toast.info('Preparando descarga del PDF…')

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    let newWindow = null

    if (isSafari) {
      // ABRIR PRIMERO, antes de `await`
      newWindow = window.open('', '_blank')
      if (!newWindow) {
        this.$toast.error('Por favor, habilita los popups en Safari.')
        return
      }
    }

    try {
      const response = await MovimientoService.comodato(item.id)
      const blob = response.data || response
      const filename = `comodato_${item.id}_${item.vehicle.plate_number || 'sin_matricula'}.pdf`

      if (isSafari && newWindow) {
        const url = window.URL.createObjectURL(blob)
        newWindow.location.href = url
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 1000)
      } else {
        saveAs(blob, filename)
      }
      
      this.$toast.success('PDF generado correctamente')
    } catch (error) {
      console.error('Error al descargar el comodato:', error)
      this.$toast.error('Error al descargar el PDF del comodato')
      if (newWindow) newWindow.close() // Cerrar ventana si hubo error
    }
  }
},

  computed: {
    ...mapGetters(['MOVIMIENTOS', 'CURRENT_USER_ROLES', 'LOCATIONS', 'HAS_PERMISSION']),
    pagination: {
      get: function () {
        return this.$store.getters.MOVIMIENTOS_PAG
      },
      set: function (value) {
        this.$store.commit('SET_MOVIMIENTOS_PAG', value)
      }
    },
    viewMode: {
      get() {
        if (this.manualViewMode) return this.manualViewMode
        return this.$vuetify.breakpoint.mdAndUp ? 'table' : 'cards'
      },
      set(value) {
        this.manualViewMode = value
      }
    },
    paginationText () {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(start + this.pagination.itemsPerPage - 1, this.totalItems)
      return `${start}-${end} de ${this.totalItems}`
    },
    searchTerm () {
      return this.filter.search
    },
    hasActiveFilters () {
      return Object.values(this.filter).some(value =>
        value !== null && value !== undefined && value !== ''
      )
    }
  },

  watch: {
    pagination: {
      handler: _.debounce(function (newVal, oldVal) {
        const relevantPropsChanged = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'].some(
          prop => !_.isEqual(newVal[prop], oldVal?.[prop])
        )
        if (relevantPropsChanged && !this.loadingItems) {
          this.loadData()
        }
      }, 200), // pequeño delay
      deep: true
    },

    searchTerm () {
      this.debouncedLoadData()
    },
    viewMode (newVal, oldVal) {
    },
    manualViewMode() {
      this.tableId++
    }
  }
}
</script>

<style scoped>
.movement-card {
  border-radius: 8px;
  overflow: hidden;
}

.stat-card {
  border-radius: 8px;
  transition: all 0.3s;
}

.stat-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-3px);
}

.movement-item-card {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
  border-radius: 8px;
}

.movement-item-card:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.action-buttons button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-buttons button:hover {
  opacity: 1;
}

/* Animación para nuevos elementos */
@keyframes highlight {
  0% {
    background-color: rgba(var(--v-primary-base), 0.1);
  }
  100% {
    background-color: transparent;
  }
}

.new-item {
  animation: highlight 2s ease-in-out;
}

.search-input-mobile {
  max-width: 200px;
}
</style>
